import React, { useMemo, useState } from "react";
import {
  Configure,
  InstantSearch,
  useHits,
  useSearchBox,
} from "react-instantsearch-hooks-web";
import algoliasearch from "algoliasearch/lite";
import { Mail, Plus, Trash2, Upload } from "lucide-react";
import DialogWrapper from "../../custom-ui/dialog/Dialog";
import CommunityAvatar from "../shared/CommunityAvatar";
import {
  addHubMember,
  getInitials,
  getUserRecordDisplayName,
  isSeekerRole,
  lookupUserByEmail,
  normalizeEmail,
  removeHubMember,
} from "../../../data/communityHub";
import toast from "react-hot-toast";
import { useQueryClient } from "react-query";
import { sendMarketplaceInvitations } from "../../../callable-cloud-functions/cloudFunctions";
import { useAuth } from "../../../providers/auth";

const searchClient = algoliasearch(
  process.env.REACT_APP_ALGOLIA_APP_ID!,
  process.env.REACT_APP_ALGOLIA_API_KEY!,
);

type TabId = "members" | "add";

type BulkImportResult = {
  addedCount: number;
  alreadyMember: string[];
  notOnSeekersList: string[];
  invalidRows: string[];
  formatError?: string;
};

const CSV_ACCEPTED_MIME_TYPES = new Set([
  "text/csv",
  "text/plain",
  "application/csv",
  "application/vnd.ms-excel",
]);

const MAX_EMAIL_LENGTH = 254;
const MAX_LOCAL_PART_LENGTH = 64;

// Local part: letters/digits/._%+- ; no leading/trailing dots.
// Domain labels may use hyphen; TLD must be at least 2 letters (e.g. .com).
const EMAIL_FORMAT_REGEX =
  /^[a-zA-Z0-9](?:[a-zA-Z0-9._%+-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

function isValidEmailFormat(value: string) {
  const email = value.trim();
  if (!email || email.length > MAX_EMAIL_LENGTH) return false;
  if (!EMAIL_FORMAT_REGEX.test(email)) return false;

  const atIndex = email.lastIndexOf("@");
  const localPart = email.slice(0, atIndex);
  const domainPart = email.slice(atIndex + 1);

  if (localPart.length > MAX_LOCAL_PART_LENGTH) return false;
  // Reject consecutive dots in local or domain (e.g. a..b@x.com, a@b..com)
  if (localPart.includes("..") || domainPart.includes("..")) return false;
  return true;
}

function validateCsvFile(file: File): string | null {
  const fileName = file.name.toLowerCase();
  if (!fileName.endsWith(".csv")) {
    return "Please upload a file with a .csv extension.";
  }
  if (file.type && !CSV_ACCEPTED_MIME_TYPES.has(file.type)) {
    return "Invalid file type. Upload a CSV file with seeker emails.";
  }
  return null;
}

function parseCsvEmails(text: string): {
  validEmails: string[];
  invalidRows: string[];
} {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length === 0) {
    return { validEmails: [], invalidRows: [] };
  }

  const first = lines[0].toLowerCase();
  const start = first.includes("email") ? 1 : 0;
  const validEmails: string[] = [];
  const invalidRows: string[] = [];

  for (const line of lines.slice(start)) {
    const cell = line.split(",")[0]?.trim().replace(/^"|"$/g, "") || "";
    if (!cell) continue;
    if (isValidEmailFormat(cell)) {
      validEmails.push(cell.toLowerCase());
    } else {
      invalidRows.push(cell);
    }
  }

  return { validEmails, invalidRows };
}

function SectionLabel({ children }: { children: any }) {
  return (
    <p className="text-[11px] font-semibold text-[#15415E] uppercase tracking-wide mb-2">
      {children}
    </p>
  );
}

function TabBar({
  activeTab,
  onTabChange,
}: {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
}) {
  return (
    <div className="flex p-1 bg-[#EEF2F4] rounded-lg mb-5">
      <button
        type="button"
        onClick={() => onTabChange("members")}
        className={`flex-1 py-2 text-sm font-semibold rounded-md transition-colors ${
          activeTab === "members"
            ? "bg-white text-[#15415E] shadow-sm"
            : "text-gray-500 hover:text-[#15415E]"
        }`}
      >
        Members
      </button>
      <button
        type="button"
        onClick={() => onTabChange("add")}
        className={`flex-1 py-2 text-sm font-semibold rounded-md transition-colors ${
          activeTab === "add"
            ? "bg-white text-[#15415E] shadow-sm"
            : "text-gray-500 hover:text-[#15415E]"
        }`}
      >
        Add New Member
      </button>
    </div>
  );
}

function MembersTab({
  hubId,
  members,
  onClose,
  onSuccess,
}: {
  hubId: string;
  members: any[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([] as string[]);
  const [removing, setRemoving] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m: any) => {
      const name = (m.name || "").toLowerCase();
      const email = (m.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [members, search]);

  const toggleMember = (id: string) => {
    setSelectedIds((prev: string[]) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleRemove = async () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one member to remove");
      return;
    }
    setRemoving(true);
    try {
      for (const id of selectedIds) {
        await removeHubMember(hubId, id);
      }
      const removedCount = selectedIds.length;
      toast.success(
        removedCount === 1
          ? "1 member removed"
          : `${removedCount} members removed`,
      );
      setSelectedIds([]);
      onSuccess();
    } catch {
      toast.error("Failed to remove members");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="space-y-4">
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search members by name..."
        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-[#15415E] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#15415E]/30"
      />

      <div className="max-h-[320px] overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
        {filtered.length === 0 && (
          <p className="text-sm text-gray-500 py-8 text-center">
            {members.length === 0 ? "No members yet" : "No members match your search"}
          </p>
        )}
        {filtered.map((member: any) => {
          const displayName = member.name || member.email || "—";
          const checked = selectedIds.includes(member.id);

          return (
            <label
              key={member.id}
              className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleMember(member.id)}
                className="w-4 h-4 rounded border-gray-300 text-instant-teams-teal-Main focus:ring-instant-teams-teal-Main shrink-0"
              />
              <CommunityAvatar
                initials={getInitials(displayName)}
                name={displayName}
                avatarUrl={member.photoURL}
                size={40}
              />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-[#15415E] truncate">
                  {displayName}
                </p>
                <p className="text-xs text-gray-500 truncate">{member.email}</p>
              </div>
            </label>
          );
        })}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2 text-sm font-medium text-[#15415E] bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-150"
        >
          Close
        </button>
        <RemoveFooterButton
          removing={removing}
          disabled={selectedIds.length === 0}
          onRemove={handleRemove}
        />
      </div>
    </div>
  );
}

function RemoveFooterButton({
  removing,
  disabled,
  onRemove,
}: {
  removing: boolean;
  disabled: boolean;
  onRemove: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled || removing}
      onClick={onRemove}
      className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-[#E57373] rounded-lg disabled:opacity-40 hover:opacity-90 transition-opacity"
    >
      <Trash2 size={16} />
      {removing ? "Removing…" : "Remove"}
    </button>
  );
}

function SeekerSearchBox() {
  const { refine } = useSearchBox();
  return (
    <input
      type="search"
      placeholder="Type a name to search..."
      onChange={(e) => refine(e.target.value)}
      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-[#15415E] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#15415E]/30"
    />
  );
}

function SeekerSearchResults({
  excludeEmails,
  onAdd,
  addingId,
}: {
  excludeEmails: string[];
  onAdd: (hit: any) => void;
  addingId: string | null;
}) {
  const { hits } = useHits();
  const filtered = hits.filter(
    (h: any) => !excludeEmails.includes(normalizeEmail(h.email)),
  );

  if (filtered.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-4 text-center border border-gray-100 rounded-lg">
        Search to find seekers to add
      </p>
    );
  }

  return (
    <div className="max-h-[200px] overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
      {filtered.map((hit: any) => {
        const id = hit.objectID;
        const name = hit.name || hit.email || "—";
        const isAdding = addingId === id;

        return (
          <div
            key={id}
            className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50"
          >
            <CommunityAvatar
              initials={getInitials(name)}
              name={name}
              avatarUrl={hit.photoURL}
              size={36}
            />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm text-[#15415E] truncate">{name}</p>
              <p className="text-xs text-gray-500 truncate">{hit.email}</p>
            </div>
            <button
              type="button"
              disabled={isAdding}
              onClick={() => onAdd(hit)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-semibold text-[#15415E] bg-white border border-gray-200 rounded-lg shrink-0 transition-colors duration-150 hover:bg-[#E4EBEF] hover:border-[#1E596D]/40 disabled:opacity-50"
            >
              <Plus size={14} strokeWidth={2.5} />
              {isAdding ? "Adding…" : "Add"}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function NotYetOnInstantTeamsCard({
  emails,
  hubName,
  invitedByName,
  onInvited,
}: {
  emails: string[];
  hubName?: string;
  invitedByName?: string;
  onInvited: () => void;
}) {
  const [sending, setSending] = useState(false);

  const handleSendInvitations = async () => {
    setSending(true);
    try {
      const result: any = await sendMarketplaceInvitations({
        emails,
        hubName,
        invitedByName,
      });
      const data = result?.data;
      const sentCount = data?.sentCount ?? 0;

      if (sentCount > 0) {
        toast.success(
          sentCount === 1
            ? "Invitation sent successfully"
            : `${sentCount} invitations sent successfully`,
        );
        onInvited();
      } else {
        toast.error(
          data?.message || "No invitations were sent. These users may already be members.",
        );
      }
    } catch (err: any) {
      console.error("sendMarketplaceInvitations failed:", err);
      const message =
        err?.message ||
        err?.details ||
        (typeof err?.code === "string" ? err.code : null) ||
        "Failed to send invitations";
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-[#E8E4DF] bg-[#FAF8F5] px-4 py-3">
      <p className="font-semibold text-[#15415E] text-sm">
        Not yet on Instant Teams ({emails.length})
      </p>
      <p className="mt-2 text-sm leading-relaxed text-gray-600">
        These emails aren&apos;t members yet and can&apos;t be added directly. You
        can invite them to join the Instant Teams marketplace.
      </p>
      <p className="mt-2 text-sm text-[#15415E] break-words">{emails.join(", ")}</p>
      <button
        type="button"
        disabled={sending}
        onClick={handleSendInvitations}
        className="mt-3 inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-[#15415E] bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
      >
        <Mail size={16} strokeWidth={1.75} />
        {sending ? "Sending…" : "Send invitations"}
      </button>
    </div>
  );
}

function AddNewMemberTab({
  hubId,
  hubName,
  excludeEmails,
  onSuccess,
}: {
  hubId: string;
  hubName?: string;
  excludeEmails: string[];
  onSuccess: () => void;
}) {
  const qc = useQueryClient();
  const { user }: any = useAuth();
  const [addingId, setAddingId] = useState(null as string | null);
  const [localExclude, setLocalExclude] = useState(excludeEmails);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null as BulkImportResult | null);
  const fileInputRef = React.useRef(null);
  const invitedByName = user?.name || user?.displayName || "";

  const handleAddSeeker = async (hit: any) => {
    const id = hit.objectID;
    setAddingId(id);
    try {
      const role = hit["claims.role"] || hit.claims?.role;
      const email = hit.email;
      const name = hit.name || email;

      if (!isSeekerRole(role)) {
        toast.error("Only seekers can be added as members");
        return;
      }
      if (localExclude.includes(normalizeEmail(email))) {
        toast.error("Already a member");
        return;
      }

      const wasAdded = await addHubMember(hubId, {
        email,
        name,
        photoURL: hit.photoURL,
      });
      if (!wasAdded) {
        toast.error("Already a member of this hub");
        return;
      }
      setLocalExclude((prev: string[]) => [...prev, normalizeEmail(email)]);
      toast.success("1 member successfully added");
      onSuccess();
    } catch {
      toast.error("Failed to add member");
    } finally {
      setAddingId(null);
    }
  };

  const handleCsvImport = async (file: File) => {
    setImporting(true);
    setImportResult(null);
    try {
      const fileError = validateCsvFile(file);
      if (fileError) {
        toast.error(fileError);
        setImportResult({
          addedCount: 0,
          alreadyMember: [],
          notOnSeekersList: [],
          invalidRows: [],
          formatError: fileError,
        });
        return;
      }

      const text = await file.text();
      if (!text.trim()) {
        const formatError = "The file is empty or could not be read as CSV.";
        toast.error(formatError);
        setImportResult({
          addedCount: 0,
          alreadyMember: [],
          notOnSeekersList: [],
          invalidRows: [],
          formatError,
        });
        return;
      }

      const { validEmails, invalidRows } = parseCsvEmails(text);
      if (!validEmails.length && !invalidRows.length) {
        const formatError =
          "No emails found in CSV. Use one email per line or a column named email.";
        toast.error(formatError);
        setImportResult({
          addedCount: 0,
          alreadyMember: [],
          notOnSeekersList: [],
          invalidRows: [],
          formatError,
        });
        return;
      }

      let addedCount = 0;
      const alreadyMember: string[] = [];
      const notOnSeekersList: string[] = [];
      const excludeSet = new Set(localExclude);

      for (const email of validEmails) {
        const normalized = normalizeEmail(email);
        if (excludeSet.has(normalized)) {
          alreadyMember.push(email);
          continue;
        }
        const user: any = await lookupUserByEmail(email);
        const role = user?.claims?.role;
        if (!user || !isSeekerRole(role)) {
          notOnSeekersList.push(email);
          continue;
        }
        const name = getUserRecordDisplayName(user) || email;
        await addHubMember(hubId, {
          email: user?.email || email,
          name,
          photoURL: user?.photoURL,
        });
        excludeSet.add(normalized);
        addedCount += 1;
      }

      setLocalExclude(Array.from(excludeSet));
      if (addedCount > 0) {
        qc.invalidateQueries(["communityHub", hubId]);
        qc.invalidateQueries(["communityHub"]);
        qc.invalidateQueries(["hubMembers", hubId]);
        onSuccess();
      }

      setImportResult({
        addedCount,
        alreadyMember,
        notOnSeekersList,
        invalidRows,
      });

      if (!addedCount && !validEmails.length && invalidRows.length > 0) {
        toast.error("No valid email addresses found in CSV");
      }
    } catch {
      const formatError =
        "Import failed. The file may be corrupted or not a valid CSV.";
      toast.error(formatError);
      setImportResult({
        addedCount: 0,
        alreadyMember: [],
        notOnSeekersList: [],
        invalidRows: [],
        formatError,
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <SectionLabel>Search seekers</SectionLabel>
        <InstantSearch searchClient={searchClient} indexName="UserQueries">
          <Configure filters="claims.role:SKR" hitsPerPage={20} />
          <div className="space-y-3">
            <SeekerSearchBox />
            <SeekerSearchResults
              excludeEmails={localExclude}
              onAdd={handleAddSeeker}
              addingId={addingId}
            />
          </div>
        </InstantSearch>
      </div>

      <div>
        <SectionLabel>Bulk import</SectionLabel>

        {importResult && (
          <div className="mb-4 rounded-lg border border-[#15415E]/15 bg-[#F4F7F8] px-4 py-3 text-sm text-[#15415E]">
            {importResult.formatError ? (
              <p className="font-semibold leading-snug text-red-700">
                {importResult.formatError}
              </p>
            ) : (
              <p className="font-semibold leading-snug">
                {importResult.addedCount}{" "}
                {importResult.addedCount === 1 ? "member" : "members"} successfully
                added
              </p>
            )}
            {importResult.invalidRows.length > 0 && (
              <p className="mt-2 leading-snug text-gray-600">
                <span className="font-semibold text-[#15415E]">
                  Invalid email format:
                </span>{" "}
                {importResult.invalidRows.join(", ")}
              </p>
            )}
            {importResult.alreadyMember.length > 0 && (
              <p className="mt-2 leading-snug text-gray-600">
                <span className="font-semibold text-[#15415E]">
                  Already a member of this hub:
                </span>{" "}
                {importResult.alreadyMember.join(", ")}
              </p>
            )}
            {importResult.notOnSeekersList.length > 0 && (
              <NotYetOnInstantTeamsCard
                emails={importResult.notOnSeekersList}
                hubName={hubName}
                invitedByName={invitedByName}
                onInvited={() =>
                  setImportResult((prev: BulkImportResult | null) =>
                    prev ? { ...prev, notOnSeekersList: [] } : prev,
                  )
                }
              />
            )}
          </div>
        )}

        <div className="border-2 border-dashed border-gray-200 rounded-xl px-6 py-8 flex flex-col items-center text-center">
          <Upload className="text-gray-500 mb-3" size={28} strokeWidth={1.5} />
          <p className="text-sm text-gray-600 max-w-sm leading-relaxed mb-4">
            Upload a .csv file with seeker emails (one per line or
            comma-separated).
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            disabled={importing}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleCsvImport(f);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            disabled={importing}
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 text-sm font-medium text-[#15415E] bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {importing ? "Importing…" : "Choose CSV file"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ManageMembersModal({
  hubId,
  hubName,
  members,
  excludeEmails,
  onClose,
  onSuccess,
}: {
  hubId: string;
  hubName?: string;
  members: any[];
  excludeEmails: string[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [activeTab, setActiveTab] = useState("members");

  return (
    <DialogWrapper
      title="Manage members"
      size="max-w-lg"
      titleColor=""
      titleStyle={{
        background:
          "linear-gradient(135deg, rgb(21, 65, 94) 0%, rgb(30, 89, 109) 100%)",
      }}
      onClose={onClose}
      displayCloseIcon
    >
      <div className="-mt-2">
        <TabBar activeTab={activeTab} onTabChange={setActiveTab} />

        {activeTab === "members" ? (
          <MembersTab
            hubId={hubId}
            members={members}
            onClose={onClose}
            onSuccess={onSuccess}
          />
        ) : (
          <>
            <AddNewMemberTab
              hubId={hubId}
              hubName={hubName}
              excludeEmails={excludeEmails}
              onSuccess={onSuccess}
            />
            <div className="flex justify-end pt-4 mt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 text-sm font-medium text-[#15415E] bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-150"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </DialogWrapper>
  );
}
